/**
 * ShellFrame — responsive app frame shared by AppShell (hotel) and PlatformShell (super admin).
 * Desktop: fixed 240px sidebar (collapsible to 60px) · Tablet: 60px icon rail (expandable)
 * Mobile: hidden sidebar → #021F21 top bar with <Menu/>, 280px slide-in drawer + overlay.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Menu, Bell, User, X } from "lucide-react";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useResponsiveTables } from "@/hooks/useResponsiveTables";

const ShellCtx = createContext({ bp: "desktop", collapsed: false, toggle: () => {}, mobileOpen: false, openMobile: () => {}, closeMobile: () => {} });
export const useShell = () => useContext(ShellCtx);

const KEY = "hocp.sidebar.collapsed";

export function ShellFrame({ renderSidebar, topbar, mobileActions, onBell, onUser, children, contentClassName = "", testid = "app-shell" }) {
  const bp = useBreakpoint();
  useResponsiveTables();
  const loc = useLocation();
  const [pref, setPref] = useState(() => localStorage.getItem(KEY));
  const collapsed = bp === "mobile" ? false : pref == null ? bp === "tablet" : pref === "1";
  const toggle = useCallback(() => { const n = collapsed ? "0" : "1"; localStorage.setItem(KEY, n); setPref(n); }, [collapsed]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const openMobile = useCallback(() => setMobileOpen(true), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const touchX = useRef(null);

  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);
  useEffect(() => { if (bp !== "mobile") setMobileOpen(false); }, [bp]);
  useEffect(() => { document.getElementById("main-scroll")?.scrollTo({ top: 0 }); }, [loc.pathname]);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => { if (touchX.current != null && touchX.current - e.changedTouches[0].clientX > 60) closeMobile(); touchX.current = null; };

  const ctx = { bp, collapsed, toggle, mobileOpen, openMobile, closeMobile };
  const sidebar = renderSidebar({ collapsed, mobile: bp === "mobile", close: closeMobile, toggle });

  return (
    <ShellCtx.Provider value={ctx}>
      <div className="flex h-screen overflow-hidden bg-hocp-ghost text-hocp-textPrimary" data-testid={testid} data-bp={bp}>
        {bp !== "mobile" ? (
          <aside className={`fixed top-0 left-0 h-screen z-[100] flex flex-col overflow-y-auto overflow-x-hidden bg-hocp-midnight on-dark transition-[width] duration-200 ${collapsed ? "w-[60px]" : "w-[240px]"}`}
            data-testid="sidebar" data-collapsed={collapsed ? "true" : "false"}>
            {sidebar}
          </aside>
        ) : (
          <>
            <div className={`fixed inset-0 z-[999] bg-[rgba(2,31,33,0.7)] transition-opacity duration-200 ${mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              onClick={closeMobile} data-testid="sidebar-overlay" aria-hidden />
            <aside className={`fixed top-0 left-0 h-screen w-[280px] max-w-[85vw] z-[1000] flex flex-col bg-hocp-midnight on-dark transition-transform duration-[250ms] ease-out ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
              onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="sidebar" data-mobile-open={mobileOpen ? "true" : "false"} aria-hidden={!mobileOpen}>
              <button type="button" onClick={closeMobile} className="absolute top-2 right-2 w-11 h-11 flex items-center justify-center rounded-lg text-hocp-softIce hover:bg-hocp-deepTeal" data-testid="sidebar-close" aria-label="Close menu">
                <X size={20} strokeWidth={1.5} />
              </button>
              {sidebar}
            </aside>
          </>
        )}

        <div className={`flex-1 min-w-0 flex flex-col h-screen overflow-y-auto overflow-x-hidden transition-[margin] duration-200 ${bp === "mobile" ? "ml-0" : collapsed ? "ml-[60px]" : "ml-[240px]"}`}
          data-testid="main-scroll" id="main-scroll">
          {bp === "mobile" ? (
            <header className="sticky top-0 z-40 h-14 bg-hocp-midnight text-hocp-softIce flex items-center px-1 gap-1 on-dark" data-testid="mobile-topbar">
              <button type="button" onClick={openMobile} className="w-11 h-11 flex items-center justify-center rounded-lg hover:bg-hocp-deepTeal" data-testid="mobile-menu-button" aria-label="Open menu">
                <Menu size={22} strokeWidth={1.5} />
              </button>
              <div className="flex-1 text-center font-display font-bold text-[17px] text-white tracking-tight" data-testid="mobile-brand">HOCP</div>
              {mobileActions}
              <button type="button" onClick={onBell} className="w-11 h-11 flex items-center justify-center rounded-lg hover:bg-hocp-deepTeal" data-testid="mobile-bell" aria-label="Notifications">
                <Bell size={20} strokeWidth={1.5} />
              </button>
              <button type="button" onClick={onUser} className="w-11 h-11 flex items-center justify-center rounded-lg hover:bg-hocp-deepTeal" data-testid="mobile-user" aria-label="Profile">
                <User size={20} strokeWidth={1.5} />
              </button>
            </header>
          ) : topbar}
          <main className="flex-1 min-w-0">
            <div className={`max-w-[1600px] mx-auto px-4 py-4 md:px-6 md:py-6 lg:px-8 lg:py-8 animate-fade-in-up ${contentClassName}`}>
              {children}
            </div>
          </main>
        </div>
      </div>
    </ShellCtx.Provider>
  );
}
